import React, { ReactNode } from 'react';

import { renderToString } from 'react-dom/server';
import { ThemeProvider } from 'styled-components';

import { StyledTheme } from 'theme';

import { TextInputProps } from './types';
import { StyledContainer, TextComponent } from './styles';

const EMPTY_KEY_HANDLERS: { [key: string]: (event: React.KeyboardEvent, position: number) => void } = {};

function normalizeHtml(str: string): string {
  return str && str.replace(/&nbsp;|\u202F|\u00A0/g, ' ');
}

const TextInput: React.FunctionComponent<TextInputProps> = ({
  disabled,
  autoFocus,
  multiLine,
  onChange,
  onEnter,
  onEscape,
  onKey = EMPTY_KEY_HANDLERS,
  placeholder = '',
  componentRef: componentExternalRef,
  value,
  ...props
}) => {
  const componentInnerRef = React.useRef(null);
  const componentRef = componentExternalRef || componentInnerRef;
  const [caretPosition, setCaretPosition] = React.useState<number | null>(null);
  const lastKnownCaret = React.useRef(0);

  const getPosition = React.useCallback(
    (defaultOldValue = true): number => {
      const selection = window.getSelection();
      if (selection === null) {
        throw new Error('Position cannot be calculated');
      }
      if (selection.anchorNode === componentRef.current && defaultOldValue) {
        return lastKnownCaret.current;
      }

      let position = 0;
      const nodes = componentRef.current?.childNodes || [];
      for (const node of nodes) {
        if (node === selection.anchorNode) {
          position += node.textContent?.slice(0, selection.anchorOffset).replace(/\uFEFF/g, '').length || 0;
          break;
        } else if (node.nodeType === 3) {
          position += node.textContent?.replace(/\uFEFF/g, '').length || 0;
          // @ts-ignore
        } else if (node.nodeType === 1 && node.attributes.getNamedItem('data-value') !== null) {
          // @ts-ignore
          position += node.attributes.getNamedItem('data-value').value.length;
        }
      }
      lastKnownCaret.current = position;
      return position;
    },
    [componentRef]
  );

  const onKeyPress = (event: React.KeyboardEvent): void => {
    if (event.key === 'Enter') {
      if (!event.shiftKey && onEnter) {
        onEnter(event, getPosition());
        event.preventDefault();
      } else {
        getPosition();
        lastKnownCaret.current += 1;
      }
    }
  };

  const onKeyDown = (event: React.KeyboardEvent): void => {
    if (event.key in onKey) {
      onKey[event.key as string](event, getPosition());
    }
    if (event.key === 'Escape') {
      onEscape && onEscape(event, getPosition());
      event.preventDefault();
    }
  };

  const valueToRender = React.useMemo(() => {
    let result = value === '' ? [] : [...(value instanceof Array ? value : [value])];

    if (result.length > 0) {
      result = ['\uFEFF', ...result];
    }

    if (
      (result.length === 0 ||
        result[result.length - 1] instanceof Object ||
        (result[result.length - 1] as string | undefined)?.slice(-1) !== '\n') &&
      multiLine
    ) {
      result = [...result, '\n'];
    }

    return result;
  }, [multiLine, value]);

  React.useLayoutEffect(() => {
    if (componentRef.current !== null) {
      // @ts-ignore
      const potentialValue = renderToString(<ThemeProvider theme={StyledTheme}>{valueToRender}</ThemeProvider>);
      if (potentialValue !== normalizeHtml(componentRef.current.innerHTML)) {
        componentRef.current.innerHTML = potentialValue;
      }
    }

    if (caretPosition !== null) {
      const nodes = componentRef.current?.childNodes || [];
      let position = 0;
      for (const node of nodes) {
        let nodeSize = 0;
        if (node.nodeType === 3) {
          nodeSize += node.textContent?.replace(/\uFEFF/g, '').length || 0;
          // @ts-ignore
        } else if (node.nodeType === 1 && node.attributes.getNamedItem('data-value') !== null) {
          // @ts-ignore
          nodeSize += node.attributes.getNamedItem('data-value').value.length;
        }
        if (node.nodeType === 3 && (caretPosition as number) - position <= nodeSize) {
          const range = new Range();
          let pos = (caretPosition as number) - position;
          pos = pos < 0 ? 0 : pos;

          range.setStart(node, pos);
          range.collapse(true);
          window.getSelection()?.removeAllRanges();
          window.getSelection()?.addRange(range);
          break;
        } else {
          position += nodeSize;
        }
      }
    }
  }, [value, caretPosition, componentRef, getPosition, valueToRender]);

  const onInput = (): void => {
    setCaretPosition(getPosition());
    let newText = '';

    for (const child of componentRef.current?.childNodes || []) {
      if (child.nodeType === 3) {
        const text = child.textContent?.replace(/\uFEFF/g, '');

        if (text === '\n' && !multiLine) {
          return;
        }
        if (text && text?.length > 0) {
          if (text !== value || newText !== text) {
            newText += text;
          } else {
            newText += text.slice(1);
          }
        }
        // @ts-ignore
      } else if (child.nodeType === 1 && child.attributes.getNamedItem('data-value') !== null) {
        // @ts-ignore
        newText += child.attributes.getNamedItem('data-value').value;
      } else if (child.nodeType === 1 && (child as unknown as { tagName: string }).tagName === 'BR' && multiLine) {
        newText += '\n';
      }
    }

    onChange(newText, getPosition());
  };

  React.useEffect(() => {
    if (autoFocus && componentRef?.current) {
      componentRef.current.focus();
      setCaretPosition((value as ReactNode[]).length);
    }
  }, [componentRef, autoFocus]);

  return (
    <StyledContainer onKeyDown={onKeyDown} onKeyPress={onKeyPress} role="none" {...props}>
      <TextComponent
        disabled={disabled}
        contentEditable={!disabled}
        data-placeholder={placeholder}
        $empty={!(value as unknown[] | undefined)?.length || (value as unknown[])[0] === '\n'}
        ref={componentRef}
        onInput={onInput}
      />
    </StyledContainer>
  );
};

export default TextInput;
